import dayjs, {Dayjs} from "dayjs";
import {CompanyAddressDTO} from "@dto/CompanyAddressDTO";
import {OrdersListDTO} from "@dto/OrdersListDTO";
import {ORDERS_CLOSED_MARGIN, ORDERS_CLOSED_REFRESH_INTERVAL, ORDERS_REFRESH_INTERVAL} from "@src/config";

export type WorkDay = CompanyAddressDTO['workTime'][number]

const DAYS_IN_WEEK = 7
const WORKING_DAY_STATUS = 1
const MINUTES_IN_HOUR = 60

/**
 * The slow interval only when the address is surely closed and nothing is live; any doubt (no work time,
 * a malformed time, the list not loaded yet) keeps the normal 60 s.
 */
export const resolveOrdersRefreshInterval = (
    ordersList: OrdersListDTO | undefined,
    workTime: WorkDay[] | undefined,
    now: Dayjs = dayjs(),
): number => {
    if (!ordersList) return ORDERS_REFRESH_INTERVAL
    if (ordersList.items.length > 0) return ORDERS_REFRESH_INTERVAL
    if (!isAddressClosedForOrders(workTime, now)) return ORDERS_REFRESH_INTERVAL

    return ORDERS_CLOSED_REFRESH_INTERVAL
}

/**
 * Closed now, and also ORDERS_CLOSED_MARGIN ago and in ORDERS_CLOSED_MARGIN.
 * workTime: 7 days from Monday, "HH:mm", status 0 = day off (as livesite's WorkTimeResolver).
 */
export const isAddressClosedForOrders = (workTime: WorkDay[] | undefined, now: Dayjs): boolean => {
    if (!workTime) return false
    if (workTime.length !== DAYS_IN_WEEK) return false

    const marginMinutes = ORDERS_CLOSED_MARGIN / 1000 / 60
    if (isOpenAt(workTime, now)) return false
    if (isOpenAt(workTime, now.subtract(marginMinutes, 'minute'))) return false
    if (isOpenAt(workTime, now.add(marginMinutes, 'minute'))) return false

    return true
}

const isOpenAt = (workTime: WorkDay[], time: Dayjs): boolean => {
    const todayIndex = (time.day() + 6) % DAYS_IN_WEEK
    const yesterdayIndex = (todayIndex + 6) % DAYS_IN_WEEK
    const minutesOfDay = time.hour() * MINUTES_IN_HOUR + time.minute()

    if (isOpenTodayAt(workTime[todayIndex], minutesOfDay)) return true

    return isOpenAfterMidnightOfYesterday(workTime[yesterdayIndex], minutesOfDay)
}

const isOpenTodayAt = (workDay: WorkDay | undefined, minutesOfDay: number): boolean => {
    if (!workDay) return false
    if (workDay.status !== WORKING_DAY_STATUS) return false

    const startMinutes = parseMinutes(workDay.start)
    const endMinutes = parseMinutes(workDay.end)
    // A time that cannot be read: assume open.
    if (startMinutes === null || endMinutes === null) return true
    // Same start and end: open all day.
    if (startMinutes === endMinutes) return true

    if (startMinutes < endMinutes) {
        return minutesOfDay >= startMinutes && minutesOfDay < endMinutes
    }

    // Past midnight (e.g. 18:00–02:00): today's part runs until midnight.
    return minutesOfDay >= startMinutes
}

const isOpenAfterMidnightOfYesterday = (yesterday: WorkDay | undefined, minutesOfDay: number): boolean => {
    if (!yesterday) return false
    if (yesterday.status !== WORKING_DAY_STATUS) return false

    const startMinutes = parseMinutes(yesterday.start)
    const endMinutes = parseMinutes(yesterday.end)
    if (startMinutes === null || endMinutes === null) return true
    if (startMinutes <= endMinutes) return false

    return minutesOfDay < endMinutes
}

const parseMinutes = (time: string | undefined): number | null => {
    if (!time) return null

    const match = /^(\d{1,2}):(\d{2})/.exec(time)
    if (!match) return null

    const hours = Number(match[1])
    const minutes = Number(match[2])
    if (hours > 24 || minutes > 59) return null

    return hours * MINUTES_IN_HOUR + minutes
}
